<script setup lang="ts" generic="T extends string">
import type { Component } from "vue";

export type FilterTabOption<K extends string = string> = {
  id: K;
  label: string;
  count?: number;
  icon?: Component;
  variant?: "dark" | "yellow" | "blue" | "purple" | "red";
};

type Props = {
  options: FilterTabOption<T>[];
};

const model = defineModel<T>({ required: true });
const { options } = defineProps<Props>();

function getPillClasses(option: FilterTabOption<T>): string {
  const isSelected = model.value === option.id;
  const variant = option.variant || "dark";

  if (isSelected) {
    switch (variant) {
      case "yellow":
        return "translate-y-0.5 border-kekkle-dark bg-kekkle-yellow text-kekkle-dark shadow-none";
      case "blue":
        return "translate-y-0.5 border-kekkle-dark bg-kekkle-blue text-kekkle-dark shadow-none";
      case "purple":
        return "translate-y-0.5 border-kekkle-dark bg-kekkle-purple text-white shadow-none";
      case "red":
        return "translate-y-0.5 border-kekkle-dark bg-kekkle-red text-white shadow-none";
      case "dark":
      default:
        return "translate-y-0.5 border-kekkle-dark bg-kekkle-dark text-white shadow-none dark:bg-slate-700";
    }
  } else {
    switch (variant) {
      case "yellow":
        return "border-kekkle-dark bg-kekkle-yellow-light text-kekkle-dark shadow-yellow-hard-sm hover:bg-kekkle-yellow/80";
      case "blue":
        return "border-kekkle-dark bg-kekkle-blue-light text-kekkle-dark shadow-blue-hard-sm hover:bg-kekkle-blue/80";
      case "purple":
        return "border-kekkle-dark bg-kekkle-purple-light text-kekkle-dark shadow-purple-hard-sm hover:bg-kekkle-purple/80";
      case "red":
        return "border-kekkle-dark bg-kekkle-red-light text-kekkle-dark shadow-red-hard-sm hover:bg-kekkle-red/80";
      case "dark":
      default:
        return "border-kekkle-dark bg-kekkle-purple-light text-kekkle-dark shadow-purple-hard-sm hover:bg-kekkle-purple/80 dark:bg-kekkle-dark-light dark:text-slate-200";
    }
  }
}

function getBadgeClasses(option: FilterTabOption<T>): string {
  const isSelected = model.value === option.id;
  if (isSelected) {
    return "bg-black/15 text-current dark:bg-white/20";
  }
  return "bg-kekkle-dark/10 text-kekkle-dark dark:bg-white/15 dark:text-slate-800";
}
</script>

<template>
  <div class="flex flex-wrap items-center justify-center gap-2 py-1">
    <button
      v-for="option in options"
      :key="option.id"
      type="button"
      class="flex items-center gap-1.5 rounded-lg border-2 p-2 px-4 text-xs font-bold transition-all duration-200"
      :class="getPillClasses(option)"
      @click="model = option.id"
    >
      <component
        :is="option.icon"
        v-if="option.icon"
        class="h-4 w-4 shrink-0"
      />
      <span>{{ option.label }}</span>
      <span
        v-if="option.count !== undefined"
        class="rounded-full px-2 py-0.5 text-[10px] font-black"
        :class="getBadgeClasses(option)"
      >
        {{ option.count }}
      </span>
    </button>
  </div>
</template>
